import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
/** @jsx jsx */
import { jsx } from '@emotion/core'
import { connect } from 'react-redux'

import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import { numCol, dateCol, intCol, strCol } from '@integec/grid-tools/lib/cols'
import { Button, DatePicker, Input, message } from 'antd'
import {
  viewHourlyDerateCauseCode,
  processHourlyDerateCauseCode,
} from 'api/pgads'
import {
  DatePickerContext,
  ErrorContext,
  SpinContext,
  UnitSelectionContext,
} from 'context'
import {
  getEnvConfig,
  getPermitActions,
  getApiTargetInfo,
  UnitSelection,
  ExportComponent,
} from 'core'
import moment from 'moment'
import {
  modifiedDateCol,
  modifierCol,
  utilityHeader,
  unitIdHeader,
} from 'core/HeaderColTypeProps'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'
import {
  getUnitNameMap,
  getUnits,
  getUtilityNameMap,
  getValidYearRange,
} from 'core/selectors'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
  isValidYearRange,
} from 'core/utils'
import * as ScreenPermissions from 'core/EventActions'
import JcTmpErrorDialog from 'core/component/JcTmpErrorDialog'

const HourlyDerateCauseCodeHeaders = [
  // strCol({ ident: 'utility', display: 'Utility', isKey: true }),
  // strCol({ ident: 'unit', display: 'Unit', isKey: true }),
  utilityHeader,
  unitIdHeader,
  dateCol({ ident: 'transdate', display: 'Transdate', isKey: true }),
  intCol({ ident: 'hour', display: 'Hour', isKey: true }),
  intCol({ ident: 'causeCode', display: 'CauseCode', isFiltered: true }),
  numCol({ ident: 'lostMwh', display: 'Lost MWH', isFiltered: true }),
  strCol({
    ident: 'description',
    display: 'Description',
    isKey: true,
    width: 500,
  }),
  modifierCol,
  modifiedDateCol,
]

const HourlyViewDerateCauseCode = ({
  apiTargetInfo,
  utilityNameMap,
  unitNameMap,
  envConfig,
  actionPermits,
  ...props
}) => {
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  const {
    selectedUnits,
    setSelectedUnits,
    setVisibleUnitSelection,
  } = useContext(UnitSelectionContext)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [data, setData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const authGodMode = envConfig.authGodMode
  const [processData, setProcessData] = useState([])
  const hasProcessPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.DERATE_CAUSECODE_PROCESS],
        matchAny: true,
      }),
    [actionPermits, authGodMode]
  )

  useEffect(
    _ => {
      setData([])
      setProcessData([])
    },
    [
      setSelectedDateRagePicker,
      selectedDateRangePicker,
      selectedUnits,
      setSelectedUnits,
    ]
  )

  const processCauseCode = ({
    dates = selectedDateRangePicker,
    selUnits = selectedUnits,
  }) => {
    const isValid = isValidYearRange(props.validYearRange, dates[0], dates[1])
    if (!isValid) return

    if (selUnits === undefined || selUnits.length < 1) {
      message.error('Please, Select Unit')
      setVisibleUnitSelection(true)
      return
    }
    setSpinning(true)
    // setVisibleUnitSelection(false)
    processHourlyDerateCauseCode(apiTargetInfo)({
      startDate: dates[0],
      endDate: dates[1],
      units: selUnits,
    }).fork(setError, data => {
      setSpinning(false)
      if (data.length <= 0) message.info('Process finished.')
      setProcessData(data)
      // setData(
      //   data.map(d => ({
      //     ...d,
      //     utility: `${d.utility}-${utilityNameMap[d.utility]}`,
      //     unitId: `${d.unit}-${unitNameMap[d.unitId]}`,
      //     zoneDateStringZone: d.zoneDateString + d.zone,
      //   }))
      // )
    })
  }

  const retrieve = ({
    dates = selectedDateRangePicker,
    selUnits = selectedUnits,
  }) => {
    if (selUnits === undefined || selUnits.length < 1) {
      message.error('Please, Select Unit')
      setVisibleUnitSelection(true)
      return
    }

    const isValid = isValidYearRange(props.validYearRange, dates[0], dates[1])
    if (!isValid) return

    setSpinning(true)
    // setVisibleUnitSelection(false)
    viewHourlyDerateCauseCode(apiTargetInfo)({
      startDate: dates[0],
      endDate: dates[1],
      units: selUnits,
    }).fork(setError, data => {
      setSpinning(false)
      // if (data.length <= 0) message.info('Process finished.')
      setData(data)
    })
  }
  const headerWithFormatter = HourlyDerateCauseCodeHeaders.map(header => {
    if (header.ident === 'utility') {
      return {
        ...header,
        rowHeaderBackgroundColor: '#eee',
        dataFormatter: ({ value }) => utilityNameMap[value] || value,
        sortIndexGetter: ({ rowData }) =>
          `${rowData.utility}-${utilityNameMap[rowData.utility]}` ||
          rowData.utility,
      }
    }
    if (header.ident === 'unitId') {
      return {
        ...header,
        rowHeaderBackgroundColor: '#eee',
        dataFormatter: ({ value }) => unitNameMap[value] || value,
        sortIndexGetter: ({ rowData }) =>
          `${rowData.utility}-${unitNameMap[rowData.unit]}` || rowData.unit,
      }
    }
    return header
  })

  const GridRef = useRef()

  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <UnitSelection retrieveData={retrieve} />

      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            width: '270px',
          }}
        >
          Hourly Derate by Cause Code
        </Title>

        <DatePicker.RangePicker
          style={{ marginLeft: '15px' }}
          allowClear={false}
          format="MM/DD/YYYY"
          value={selectedDateRangePicker}
          onChange={d => {
            setSelectedDateRagePicker(d)
            retrieve({ dates: d })
          }}
        />

        <Button
          icon="database"
          loading={spinning}
          onClick={retrieve}
          css={{ marginLeft: '10px' }}
          type="primary"
        >
          Retrieve
        </Button>
        {hasProcessPermission && (
          <Button
            icon="thunderbolt"
            type="danger"
            loading={spinning}
            onClick={processCauseCode}
            css={{ marginLeft: '10px' }}
          >
            Process
          </Button>
        )}
        <Filler />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          style={{ marginLeft: '10px', marginRight: '10px', maxWidth: '200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
          data={data}
          headers={HourlyDerateCauseCodeHeaders}
          fileName={`Hourly Derate by Cause Code ${moment().format(
            'YYYY-MM-DD HH mm ss'
          )}`}
          btnStyle={{ marginRight: '10px' }}
          disabled={data && data.length <= 0}
        />
      </CriteriaRow>
      <JcTmpErrorDialog
        errors={processData}
        visible={processData.length > 0}
        closeErrorPrompt={_ => setProcessData([])}
      />
      <FullHeightSizer heightOffset={280}>
        {({ height, width }) => (
          <Content css={{ height: height - 20, paddingTop: '10px' }}>
            <Grid
              ref={GridRef}
              fuzzyFilter={fuzzyFilter}
              sortEnabled={false}
              data={data}
              headers={headerWithFormatter}
              altBy={u => u.unitId}
              altBgColor="#ebebeb"
              render={virtualizedGridRenderer({
                autoFixColByKey: false,
                rowHeaderRender,
                height: height - 50,
                width: width - 100,
                headerRowHeight: 30,
              })}
            />
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  units: getUnits(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  validYearRange: getValidYearRange(state),
})
export default connect(mapStateToProps)(HourlyViewDerateCauseCode)
